import { ContainerElement } from '@/containerElement';
import { Heading3Element } from '@/headingElement';
import { ImageElement } from '@/imageElement';
import { ParagraphElement } from '@/paragraphElement';
import { SerializeTextLeaf } from '@/textLeaf';
import type { RSSFeedDataPodcast } from '@/types';
import { getBackgroundColorStyle } from '@/utils/background';
import { cn } from '@/utils/cn';
import type { CSSProperties, PropsWithChildren } from 'react';
import * as styles from './RSSFeed.css';
import type { RSSFeedElementProps } from './RSSFeed.types.js';
import { formatDate } from './util.js';

const RSS_FEED_DEFAULTS = {
  imageBorderRadius: '8px',
  titleFontSize: 24,
  titleFontFamily: 'Inter',
  subtitleFontSize: 14,
  subtitleFontFamily: 'Inter',
  titleTextColor: 'inherit',
  subtitleTextColor: 'inherit',
  titleFontWeight: 700,
  subtitleFontWeight: 400,
  borderRadius: '8px',
  borderWidth: '1px',
  borderColor: 'inherit',
  borderStyle: 'none',
  background: 'transparent',
};

const COLUMNS_MAP: Record<string, string> = {
  '1': 'grid-cols-1',
  '2': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2',
  '3': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  '4': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  '5': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5',
  '6': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-6',
};

export const PodcastRssFeed = (props: PropsWithChildren<RSSFeedElementProps>) => {
  const { element, attributes, onClick, children, className } = props;
  const {
    url,
    data: rawData,
    isLoading,
    gap,
    columns,
    padding,
    itemPadding,
    itemGap,
    textGap,
    imageBorderRadius,
    titleFontSize,
    titleFontFamily,
    titleTextColor,
    titleFontWeight,
    titleAlignment,
    subtitleFontSize,
    subtitleFontFamily,
    subtitleTextColor,
    subtitleFontWeight,
    subtitleAlignment,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    backgroundColor,
    imageAspectRatio,
    imageFit,
  } = element.attrs;

  const showCount = element.attrs.showCount || 5;

  const data = rawData as RSSFeedDataPodcast;
  if (!data.rss.channel) {
    return (
      <div
        className={cn(
          'grid relative w-full h-fit',
          className,
          isLoading && 'opacity-50 animate-pulse',
          COLUMNS_MAP[columns || '3'],
        )}
        {...attributes}
      >
        <p className="text-sm text-gray-900 text-left leading-tight">Unsupported RSS feed source</p>
        <p className="text-xs text-gray-500 text-left leading-tight">{url}</p>
        <pre>{JSON.stringify(rawData, null, 2)}</pre>
      </div>
    );
  }

  const shownData = data.rss.channel.item.slice(0, showCount);
  return (
    <div
      className={cn('grid relative w-full h-fit', isLoading && 'opacity-50 animate-pulse', COLUMNS_MAP[columns || '3'])}
      style={{
        gap: gap || '16px',
        padding: padding,
      }}
      {...attributes}
    >
      {shownData.map((entry) => (
        <a
          href={entry.link}
          key={entry.guid}
          className={cn('flex flex-col items-start cursor-pointer', styles.youtubeCard)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClick}
          style={
            {
              padding: itemPadding,
              gap: itemGap || '8px',

              '--card-border-radius': borderRadius || RSS_FEED_DEFAULTS.borderRadius,
              '--card-border-width': borderWidth || RSS_FEED_DEFAULTS.borderWidth,
              '--card-border-color': borderColor || RSS_FEED_DEFAULTS.borderColor,
              '--card-border-style': borderStyle || RSS_FEED_DEFAULTS.borderStyle,
              ...getBackgroundColorStyle(backgroundColor),
            } as CSSProperties
          }
        >
          <ImageElement
            element={{
              type: 'image',
              attrs: {
                src: entry['itunes:image']?.['@href'],
                width: '100%',
                height: 'auto',
                aspectRatio: imageAspectRatio || '16/9',
                objectFit: imageFit || 'cover',
                borderRadius: imageBorderRadius || RSS_FEED_DEFAULTS.imageBorderRadius,
              },
            }}
          />
          <ContainerElement
            element={{
              type: 'container',
              attrs: {
                id: 'container',
                backgroundColor: 'transparent',
                padding: '0px',
                gap: textGap || '8px',
              },
              content: [],
            }}
          >
            <Heading3Element
              element={{
                type: 'heading',
                attrs: {
                  id: 'heading3',
                  anchorId: 'heading3',
                  padding: '0px',
                  anchorTitle: 'heading3',
                  anchorIdSync: true,
                  anchorEnabled: true,
                  anchorTitleSync: true,
                  customAttributes: {},
                  textAlign: titleAlignment || 'left',
                  level: 3,
                },
                content: [
                  {
                    text: entry.title,
                  },
                ],
              }}
            >
              <SerializeTextLeaf
                leaf={{
                  text: entry.title,
                  marks: [
                    {
                      type: 'textStyle',
                      attrs: {
                        fontSize: titleFontSize || RSS_FEED_DEFAULTS.titleFontSize,
                        fontFamily: titleFontFamily || RSS_FEED_DEFAULTS.titleFontFamily,
                        color: titleTextColor || RSS_FEED_DEFAULTS.titleTextColor,
                        fontWeight: titleFontWeight || RSS_FEED_DEFAULTS.titleFontWeight,
                      },
                    },
                  ],
                }}
              />
            </Heading3Element>
            <ParagraphElement
              element={{
                type: 'paragraph',
                attrs: {
                  textAlign: subtitleAlignment || 'left',
                  padding: '0px',
                },
              }}
            >
              <SerializeTextLeaf
                leaf={{
                  text: formatDate(entry.pubDate),
                  marks: [
                    {
                      type: 'textStyle',
                      attrs: {
                        fontSize: subtitleFontSize || RSS_FEED_DEFAULTS.subtitleFontSize,
                        fontFamily: subtitleFontFamily || RSS_FEED_DEFAULTS.subtitleFontFamily,
                        color: subtitleTextColor || RSS_FEED_DEFAULTS.subtitleTextColor,
                        fontWeight: subtitleFontWeight || RSS_FEED_DEFAULTS.subtitleFontWeight,
                      },
                    },
                  ],
                }}
              />
            </ParagraphElement>
          </ContainerElement>
        </a>
      ))}
      {children}
    </div>
  );
};
